import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Loader2, Users, ChefHat, Truck } from 'lucide-react';
import { positionStyle } from '../../../lib/scheduleFormat';
import { toast } from 'sonner';

// Behaviour follows the position, not the person: one login can hold several,
// and only people with at least one position appear in the builder.
const StaffPositionsCard = ({ staff, positions, onSave }) => {
  const [savingId, setSavingId] = useState(null);

  const save = async (person, patch) => {
    setSavingId(person.user_id);
    try {
      await onSave(person.user_id, {
        positions: patch.positions ?? person.positions,
        active: patch.active ?? person.active,
        notes: person.notes,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save positions');
    } finally {
      setSavingId(null);
    }
  };

  const toggle = (person, code) => {
    const next = person.positions.includes(code)
      ? person.positions.filter((c) => c !== code)
      : [...person.positions, code];
    save(person, { positions: next });
  };

  return (
    <Card data-testid="staff-positions-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5 text-amber-600" /> Who we schedule
        </CardTitle>
        <p className="text-sm text-gray-500">
          Mark each staff login as a baker, a driver, or both. A kitchen position must work inside a
          booking; a driver is scheduled against a pickup and a delivery date instead.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {staff.length === 0 && (
          <p className="text-sm text-gray-500" data-testid="staff-positions-empty">
            No staff logins yet. Add them under Users first.
          </p>
        )}
        {staff.map((person) => (
          <div
            key={person.user_id}
            className="rounded-lg border border-gray-200 p-3"
            data-testid={`staff-row-${person.user_id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium text-gray-900">{person.name}</p>
                <p className="truncate text-xs text-gray-500">{person.email}</p>
              </div>
              <div className="flex items-center gap-2">
                {savingId === person.user_id && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                <span className="text-xs text-gray-600">
                  {person.active ? 'Scheduled' : 'Not scheduled'}
                </span>
                <Switch
                  checked={person.active}
                  onCheckedChange={(value) => save(person, { active: value })}
                  data-testid={`staff-active-${person.user_id}`}
                />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {positions.map((option) => {
                const on = person.positions.includes(option.code);
                return (
                  <button
                    key={option.code}
                    type="button"
                    onClick={() => toggle(person, option.code)}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      on ? positionStyle(option.code) : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`staff-position-${person.user_id}-${option.code}`}
                  >
                    {option.kitchen_bound
                      ? <ChefHat className="mr-1 inline h-3.5 w-3.5" />
                      : <Truck className="mr-1 inline h-3.5 w-3.5" />}
                    {option.label}
                  </button>
                );
              })}
              {person.positions.length === 0 && (
                <Badge variant="outline" className="bg-gray-50 text-gray-500">No positions yet</Badge>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default StaffPositionsCard;
